import { useState } from "react";
import { Eye, EyeSlash, X, CircleNotch } from "@phosphor-icons/react";
import { useAuth } from "./AuthContext";

const LOGO_URL = "/ftp-logo.png";

// Minimal 4-colour Google "G" mark, inline so no extra asset/network call.
const GoogleG = () => (
  <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.6-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.1 8 3l5.7-5.7C34.6 6.1 29.6 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.7-.4-3.5z" />
    <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.6 15.9 18.9 13 24 13c3.1 0 5.8 1.1 8 3l5.7-5.7C34.6 6.1 29.6 4 24 4 16.3 4 9.6 8.3 6.3 14.7z" />
    <path fill="#4CAF50" d="M24 44c5.5 0 10.4-1.9 14.3-5.1l-6.6-5.6C29.6 35.1 26.9 36 24 36c-5.2 0-9.6-3.4-11.2-8l-6.6 5.1C9.5 39.6 16.2 44 24 44z" />
    <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4 5.5l6.6 5.6C40.9 36.6 44 30.8 44 24c0-1.3-.1-2.7-.4-3.5z" />
  </svg>
);

// Themed signup/login screen -- rendered as a full-screen overlay,
// native to the app's charcoal/white/Manrope theme.
export const AuthPage = ({ onClose }) => {
  const { loginEmail, signupEmail, loginGoogle } = useAuth();
  const [mode, setMode] = useState("login"); // "login" | "signup"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      if (mode === "login") await loginEmail(email, password);
      else await signupEmail(email, password);
      onClose();
    } catch (err) {
      setError(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="auth-page-overlay" onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 300,
        background: "rgba(20,20,22,0.55)", backdropFilter: "blur(4px)",
        display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
      }}>
      <div onClick={(e) => e.stopPropagation()} data-testid="auth-page-card"
        style={{
          background: "#fff", borderRadius: 16, padding: "32px 28px",
          width: "100%", maxWidth: 420, maxHeight: "92dvh", overflowY: "auto",
          boxShadow: "0 20px 60px rgba(0,0,0,0.25)", fontFamily: "'Manrope', sans-serif",
          position: "relative",
        }}>
        <button type="button" onClick={onClose} aria-label="Close" data-testid="auth-page-close"
          style={{ position: "absolute", top: 14, right: 14, background: "none", border: "none", cursor: "pointer", color: "#888" }}>
          <X size={18} weight="bold" />
        </button>

        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", marginBottom: 20 }}>
          <img src={LOGO_URL} alt="FTP" style={{ height: 64, marginBottom: 14 }} />
          <h2 style={{ margin: 0, fontSize: 24, fontWeight: 800, color: "#323233" }}>
            {mode === "login" ? "Welcome" : "Create your account"}
          </h2>
        </div>

        <button type="button" onClick={loginGoogle} data-testid="auth-google-button"
          style={{
            width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
            padding: "12px 16px", borderRadius: 10, border: "1px solid #e5e5e4",
            background: "#fff", cursor: "pointer", fontSize: 14, fontWeight: 600, color: "#323233",
            marginBottom: 16,
          }}>
          <GoogleG />Continue with Google
        </button>

        <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "16px 0", color: "#aaa", fontSize: 12 }}>
          <div style={{ flex: 1, height: 1, background: "#e5e5e4" }} />or<div style={{ flex: 1, height: 1, background: "#e5e5e4" }} />
        </div>

        <form onSubmit={submit}>
          <label style={{ display: "block", fontSize: 13, fontWeight: 700, color: "#323233", marginBottom: 6 }}>
            Email address
          </label>
          <input type="email" required value={email} data-testid="auth-email-input"
            onChange={(e) => setEmail(e.target.value)} placeholder="Enter your email address"
            style={{
              width: "100%", boxSizing: "border-box", padding: "11px 14px", borderRadius: 10,
              border: "1px solid #e5e5e4", fontSize: 14, color: "#323233", marginBottom: 14,
              fontFamily: "'Manrope', sans-serif",
            }} />

          <label style={{ display: "block", fontSize: 13, fontWeight: 700, color: "#323233", marginBottom: 6 }}>
            Password
          </label>
          <div style={{ position: "relative", marginBottom: 6 }}>
            <input type={showPassword ? "text" : "password"} required minLength={6} value={password}
              data-testid="auth-password-input" onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              style={{
                width: "100%", boxSizing: "border-box", padding: "11px 40px 11px 14px", borderRadius: 10,
                border: "1px solid #e5e5e4", fontSize: 14, color: "#323233",
                fontFamily: "'Manrope', sans-serif",
              }} />
            <button type="button" onClick={() => setShowPassword((s) => !s)} data-testid="auth-password-toggle"
              aria-label={showPassword ? "Hide password" : "Show password"}
              style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "#888" }}>
              {showPassword ? <EyeSlash size={18} /> : <Eye size={18} />}
            </button>
          </div>

          {error && <p data-testid="auth-error" style={{ color: "#b91c1c", fontSize: 12, margin: "4px 0 10px" }}>{error}</p>}

          <button type="submit" disabled={busy} data-testid="auth-submit-button"
            style={{
              width: "100%", padding: "13px 16px", borderRadius: 10, border: "none",
              background: "#323233", color: "#fff", fontSize: 14, fontWeight: 700,
              cursor: busy ? "default" : "pointer", marginTop: 8,
              display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
              opacity: busy ? 0.7 : 1,
            }}>
            {busy && <CircleNotch size={14} className="ai-scan-spinner" style={{ color: "#fff" }} />}
            {mode === "login" ? "Log in" : "Sign up"}
          </button>
        </form>

        <p style={{ textAlign: "center", fontSize: 13, color: "#888", marginTop: 16, marginBottom: 0 }}>
          {mode === "login" ? (
            <>Or{" "}
              <button type="button" onClick={() => { setMode("signup"); setError(""); }} data-testid="auth-switch-to-signup"
                style={{ background: "none", border: "none", cursor: "pointer", color: "#323233", fontWeight: 700, textDecoration: "underline", fontSize: 13, padding: 0 }}>
                sign up
              </button>
            </>
          ) : (
            <>Already have an account?{" "}
              <button type="button" onClick={() => { setMode("login"); setError(""); }} data-testid="auth-switch-to-login"
                style={{ background: "none", border: "none", cursor: "pointer", color: "#323233", fontWeight: 700, textDecoration: "underline", fontSize: 13, padding: 0 }}>
                Log in
              </button>
            </>
          )}
        </p>
      </div>
    </div>
  );
};
